import Chart from "chart.js/auto";
import type { Snapshot } from "@weather-deck/shared";

export type ChartSeries = {
  labels: string[];
  deck: (number | null)[];
  official: (number | null)[];
};

export function buildChartSeries(history: Snapshot[]): ChartSeries {
  return {
    labels: history.map((s) => new Date(s.fetchedAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })),
    deck: history.map((s) => {
      const sObj = s.sensor as unknown as { temp?: number; measurements?: { temperature?: number } } | null;
      return sObj?.temp ?? sObj?.measurements?.temperature ?? null;
    }),
    official: history.map((s) => {
      const cObj = s.weather?.current as unknown as { temp?: number; temperature?: number } | null;
      return cObj?.temp ?? cObj?.temperature ?? null;
    })
  };
}

export function renderComparisonChart(canvas: HTMLCanvasElement, history: Snapshot[]): Chart {
  const series = buildChartSeries(history);
  return new Chart(canvas, {
    type: "line",
    data: {
      labels: series.labels,
      datasets: [
        { label: "Deck (°C)", data: series.deck, borderColor: "#ff7a00", tension: 0.3 },
        { label: "DWD Official (°C)", data: series.official, borderColor: "#00a8ff", tension: 0.3 }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: { ticks: { color: AXIS_TEXT } },
        x: { ticks: { color: AXIS_TEXT } }
      }
    }
  });
}

const AXIS_TEXT = "#64748b";
const AXIS_GRID = "#eef2f7";
export const NOW_COLOR = "#ff7a00";

export type YoYChartOptions = {
  doy: number;
  currentVal: number | null;
  currentYear: number;
  label: string;
  unit: string;
  /** Human date this day-of-year refers to, e.g. "29 July". */
  dateLabel: string;
  color: string;
  borderDash?: number[];
};

/**
 * Chart config for "this calendar day, year by year".
 *
 * The line is each year's 24-hour mean for the day; the orange marker is a
 * single live reading. Those are different quantities — an afternoon reading
 * normally sits above a daily mean, which includes the night — so both series
 * are named in full and the legend is shown rather than hidden.
 */
export function buildYoYChartConfig(agg: import("@weather-deck/shared").SensorAggregate, o: YoYChartOptions) {
  const years = Object.keys(agg.yoy_matrix).sort();
  const values = years.map((y) => agg.yoy_matrix[y]?.[String(o.doy)] ?? null);
  const fmt = (v: number) => `${v.toFixed(1)}${o.unit}`;

  const datasets: any[] = [
    {
      label: `Daily mean, ${o.dateLabel}`,
      data: values,
      borderColor: o.color,
      borderWidth: 2,
      borderDash: o.borderDash ?? [],
      tension: 0.3,
      spanGaps: true,
      pointBackgroundColor: o.color,
      pointRadius: 4
    }
  ];

  const idx = years.indexOf(String(o.currentYear));
  if (o.currentVal != null && idx >= 0) {
    datasets.push({
      type: "scatter",
      label: "Right now (single reading)",
      data: years.map((_, i) => (i === idx ? o.currentVal : null)),
      backgroundColor: NOW_COLOR,
      pointRadius: 6
    });
  }

  return {
    type: "line" as const,
    data: { labels: years, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom" as const,
          labels: { usePointStyle: true, boxWidth: 8, padding: 10, color: AXIS_TEXT, font: { size: 10 } }
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y == null ? "no data" : fmt(ctx.parsed.y)}`
          }
        }
      },
      scales: {
        y: {
          ticks: { color: AXIS_TEXT, callback: (v: any) => `${v}${o.unit}` },
          grid: { color: AXIS_GRID }
        },
        x: { ticks: { color: AXIS_TEXT }, grid: { color: AXIS_GRID } }
      }
    }
  };
}

export function renderYoYChart(canvas: HTMLCanvasElement, agg: import("@weather-deck/shared").SensorAggregate, o: YoYChartOptions): Chart {
  const existing = Chart.getChart(canvas);
  if (existing) existing.destroy();
  return new Chart(canvas, buildYoYChartConfig(agg, o) as never);
}
